import { all, put, race, select, take, takeEvery } from 'redux-saga/effects'
import { List, Map } from 'immutable'
import { SUCCESS, FAILED } from 'crom-api-constants'
import { dataTableRemoveData, filterTableRemoveData, listTableRemoveData } from 'gnos-tables'
import { removeData as scrollbarRemoveData } from 'gnos-scrollbar'
import _ from 'lodash'

import config from '@config'

import IntlSaga from '../../../../services/translations/intl-saga'

import reportRibbonSaga from './components/Ribbon/saga'
import workspaceSaga from './components/Workspace/saga'

import {
    localeSelector,
    sessionIdSelector,
    accessRightsLoadedSelector,
    activeEntityReportId,
    getRibbonActiveBlocks,
    getToggleBlocksById,
    hasAccessRightsToReport,
    getDataTableIds,
    getFilterTableIds,
    getListTableIds,
    getTabsIds,
    getScrollbarsIds,
    isReportOpen,
    getAccessRightsReports,
} from './selectors'

import {
    LOAD_REPORT_MODULE_ACCESS_RIGHTS,
    REPORT_ACCESS_RIGHTS_REQUEST,
    REPORT_GET_TOKEN_REQUEST,
    OPEN_REPORT_BY_ID,
    CLOSE_REPORT_BY_ID,
    LOCALE_PREFIX,
    REPORT_LOADER_ID,
} from './constants'
import { TABS_NEW_TAB_ADDED, TABS_TAB_REMOVED } from '../../components/Tabs/constants'
import { REPORT_RIBBON_CHANGE_EVENT, BUTTONSARRAY_KEY } from './components/Ribbon/constants'
import {
    BLOCK_DATA_START_LOADING,
    BLOCK_DATA_LOADED,
    BLOCK_DATA_LOAD_FAILED,
    BLOCK_DATA_EMPTY,
    BLOCK_DATA_TRANSFORMED,
} from '../../components/Workspace/components/HocBlock/constants'
import { CONFIRM_DIALOG_ON_BUTTON_CLICK } from '../../../../components/Dialog/components/Confirm/constants'

import {
    reportAccessRightsRequest,
    reportGetTokenRequest,
    setReportModuleAccessRights,
    reportEntityUpdate,
    removeReportById,
    closeReportById,
} from './actions'
import { addTab, removeTab, setTabText, activeTab } from '../../components/Tabs/actions'
import { showLoader, hideLoader } from '../../../../services/loader/actions'
import { openDialog } from '../../../../services/dialog/actions'
import { removeData as tabsRemoveData } from '../../../../components/Tabs/actions'
import { sendMessage } from '../../../../services/socket/helpers'
import { getAppState } from '../../../../services/appState/selectors'
import { getActiveReportBlocks } from './components/Ribbon/selectors'
import { changebuttonsArrayButton } from '../../components/Ribbon/components/ButtonsArray/components/ConnectButton/actions'
import { activeItem } from '../../components/Ribbon/components/DropDown/actions'

const MODULE_KEY = config.modules.report.key

const REPORT_ACCESS_RIGHTS_REQUEST_SUCCESS = `${REPORT_ACCESS_RIGHTS_REQUEST}_${SUCCESS}`
const REPORT_ACCESS_RIGHTS_REQUEST_FAILED = `${REPORT_ACCESS_RIGHTS_REQUEST}_${FAILED}`

const REPORT_GET_TOKEN_REQUEST_SUCCESS = (reportId) => `${REPORT_GET_TOKEN_REQUEST(reportId)}_${SUCCESS}`
const REPORT_GET_TOKEN_REQUEST_FAILED = (reportId) => `${REPORT_GET_TOKEN_REQUEST(reportId)}_${FAILED}`

function isReportAdd(_action) {
    const actionType = _.get(_action, 'type', '')
    const moduleType = _.get(_action, 'data.type', '')

    return actionType === TABS_NEW_TAB_ADDED && moduleType === MODULE_KEY
}

function isReportRemove(_action) {
    const actionType = _.get(_action, 'type', '')
    const moduleType = _.get(_action, 'data.type', '')

    return actionType === TABS_TAB_REMOVED && moduleType === MODULE_KEY
}

function* setReportAppStateSaga() {
    // Initial load of active ribbon blocks from app-state
    const appState = yield select(getAppState)
    if (appState) {
        const reports = yield select(getAccessRightsReports)
        // Set report ribbon type from app-state, if the type is in access rights
        if (appState.report?.activeType && reports.get(appState.report?.activeType)) {
            const activeType = appState.report?.activeType

            yield put(activeItem(MODULE_KEY, 'reports', activeType))
        }

        // Set active ribbon buttons from app-state
        if (!_.isEmpty(appState.report?.ribbonActiveBlocks)) {
            const ribbonActiveBlocks = appState.report.ribbonActiveBlocks
            const reportBlocks = yield select(getActiveReportBlocks)
            const blocksToChange = reportBlocks
                .filter((block) => !block.get('isDisabled'))
                .filter((block) => block.get('name') !== config.modules.report.blocks.bisnodeCertificates.key) // Ignore bisnode certificates - always active
                .keySeq()
                .toList()

            const changeActions = blocksToChange
                .map((block) =>
                    put(changebuttonsArrayButton(MODULE_KEY, BUTTONSARRAY_KEY, block, ribbonActiveBlocks?.[block]))
                )
                .toJS()

            yield all(changeActions)
        }
    }

    return null
}

function* loadReportAccessRights() {
    const isLoaded = yield select(accessRightsLoadedSelector)
    if (isLoaded) return null

    const locale = yield select(localeSelector)

    yield put(showLoader(REPORT_LOADER_ID, IntlSaga.formatMessage(locale, `${LOCALE_PREFIX}.loadingText`)))

    const reportAccessRights = yield all({
        response: race({
            success: take(REPORT_ACCESS_RIGHTS_REQUEST_SUCCESS),
            failed: take(REPORT_ACCESS_RIGHTS_REQUEST_FAILED),
        }),
        request: put(reportAccessRightsRequest()),
    })
    const reportAccessRightsData = _.get(reportAccessRights, 'response.success.payload.response.body.data', {})

    // TODO TEST OF DISABLED BUTTON - if there is admin type, add test banner button
    if (!_.isNil(_.get(reportAccessRightsData, 'types.admin', null))) {
        yield put(
            setReportModuleAccessRights(
                _.set(_.cloneDeep(reportAccessRightsData), 'types.admin.blocks', [
                    ..._.get(reportAccessRightsData, 'types.admin.blocks', {}),
                    { name: 'banner', order: 500, isDisabled: true, defaultLevel: 0, availableLevels: [] },
                ])
            )
        )
    } else {
        yield put(setReportModuleAccessRights(reportAccessRightsData))
    }
    // TODO END OF TEST OF DISABLED BUTTON

    yield setReportAppStateSaga()

    yield put(hideLoader(REPORT_LOADER_ID))

    return null
}

function* addReport(_action) {
    const reportId = _.get(_action, 'data.id', null)

    const sessionId = yield select(sessionIdSelector)
    const locale = yield select(localeSelector)

    // load and add report to collection
    const result = yield all({
        response: race({
            success: take(REPORT_GET_TOKEN_REQUEST_SUCCESS(reportId)),
            failed: take(REPORT_GET_TOKEN_REQUEST_FAILED(reportId)),
        }),
        request: put(reportGetTokenRequest(reportId, sessionId, locale)),
    })

    if (result.response.failed) {
        const errorCode = _.get(result, 'response.failed.payload.error.code', 500)

        if (errorCode === 403) {
            const dialogSettings = {
                header: { textId: 'report', icon: '' },
                question: { textId: 'reportOverdrawn' },
                buttons: ['ok'],
            }

            yield all({
                answer: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('ok', reportId)),
                dialog: put(openDialog('confirm', reportId, dialogSettings)),
            })
        }

        // if token error, close real estate
        yield put(closeReportById(reportId))

        return null
    }

    // create updates for report
    // blocks start loading after this update (active set to true)
    const activeBlocks = yield select(getRibbonActiveBlocks)
    const updates = Map({
        blocks: activeBlocks.reduce((acc, blockKey) => acc.set(blockKey, Map({ active: true })), Map()),
    })

    yield put(reportEntityUpdate(reportId, updates))

    return null
}

function* removeReport(_action) {
    const reportId = _.get(_action, 'data.id', null)

    // remove report from store
    yield put(removeReportById(reportId))

    // remove data of data tables of block
    const dataTableIds = yield select(getDataTableIds, reportId)
    const dataTableRemoveDataActions = dataTableIds.map((tableId) => put(dataTableRemoveData(tableId))).toJS()
    yield all(dataTableRemoveDataActions)

    // remove data of filter tables of block
    const filterTableIds = yield select(getFilterTableIds, reportId)
    const filterTableRemoveDataActions = filterTableIds.map((tableId) => put(filterTableRemoveData(tableId))).toJS()
    yield all(filterTableRemoveDataActions)

    // remove data of list tables of block
    const listTableIds = yield select(getListTableIds, reportId)
    const listTableRemoveDataActions = listTableIds.map((tableId) => put(listTableRemoveData(tableId))).toJS()
    yield all(listTableRemoveDataActions)

    // remove data of tabs
    const tabsIds = yield select(getTabsIds, reportId)
    const tabsRemoveDataActions = tabsIds.map((tabsId) => put(tabsRemoveData(tabsId))).toJS()
    yield all(tabsRemoveDataActions)

    // remove data of scrollbars
    const scrollbarIds = yield select(getScrollbarsIds, reportId)
    const scrollbarRemoveDataActions = scrollbarIds.map((scrollbarId) => put(scrollbarRemoveData(scrollbarId))).toJS()
    yield all(scrollbarRemoveDataActions)

    return null
}

function* onRibbonButtonsChange() {
    const activeReportId = yield select(activeEntityReportId)
    if (!activeReportId) return null

    // create updates for report
    const toggleBlocks = yield select(getToggleBlocksById, activeReportId)
    const toActiveUpdates = toggleBlocks
        .get('toActive', List())
        .reduce((acc, blockKey) => acc.set(blockKey, Map({ active: true })), Map())
    const toInactiveUpdates = toggleBlocks
        .get('toInactive', List())
        .reduce((acc, blockKey) => acc.set(blockKey, Map({ active: false })), Map())

    const updates = Map({ blocks: toActiveUpdates.mergeDeep(toInactiveUpdates) })

    yield put(reportEntityUpdate(activeReportId, updates))

    return null
}

function* onBlockDataStartLoading(_action) {
    const module = _.get(_action, 'data.module', null)
    if (module !== MODULE_KEY) return null

    const reportId = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (!reportId || !blockKey) return null

    // create update for report
    const updates = Map({ blocks: Map({ [blockKey]: Map({ isLoading: true }) }) })

    yield put(reportEntityUpdate(reportId, updates))

    return null
}

function* onBlockDataLoaded(_action) {
    const module = _.get(_action, 'data.module', null)
    if (module !== MODULE_KEY) return null

    const reportId = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    const blockData = _.get(_action, 'data.data', null)
    const locale = _.get(_action, 'data.locale', null)
    if (!reportId || !blockKey) return null

    // create updates for report
    const updates = Map({
        blocks: Map({
            [blockKey]: Map({
                isLoading: false,
                isLoaded: true,
                loadedLocale: locale,
                data: blockData,
            }),
        }),
    })

    yield put(reportEntityUpdate(reportId, updates))

    return null
}

function* onBlockDataLoadEmpty(_action) {
    const module = _.get(_action, 'data.module', null)
    if (module !== MODULE_KEY) return null

    const reportId = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (!reportId || !blockKey) return null

    // create updates
    const updates = Map({
        blocks: Map({
            [blockKey]: Map({
                isLoading: false,
                isLoaded: true,
            }),
        }),
    })

    yield put(reportEntityUpdate(reportId, updates))

    return null
}

function* onBlockDataLoadFailed(_action) {
    const module = _.get(_action, 'data.module', null)
    if (module !== MODULE_KEY) return null

    const reportId = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (!reportId || !blockKey) return null

    // create updates for report
    const updates = Map({
        blocks: Map({
            [blockKey]: Map({
                isLoading: false,
                isLoaded: true,
            }),
        }),
    })

    yield put(reportEntityUpdate(reportId, updates))

    return null
}

function* onBlockDataTransformed(_action) {
    const module = _.get(_action, 'data.module', null)
    if (module !== MODULE_KEY) return null

    const reportId = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    const blockTransformedData = _.get(_action, 'data.transformedData', null)
    if (!reportId || !blockKey) return null

    // create update for report
    const updates = Map({ blocks: Map({ [blockKey]: Map({ transformedData: blockTransformedData }) }) })

    yield put(reportEntityUpdate(reportId, updates))

    return null
}

function* onOpenReportById(_action) {
    const reportId = _.get(_action, 'data.id', null)
    if (!reportId) return null

    const moduleEnabled = yield select(hasAccessRightsToReport)
    if (!moduleEnabled) return null

    // Check if the entity has been already opened for this module
    const isOpen = yield select(isReportOpen, reportId)
    if (isOpen) {
        // Open the tab with the entity (doesn't reload the whole workspace again)
        yield put(activeTab(reportId, MODULE_KEY))
        return null
    }

    yield put(addTab(reportId, MODULE_KEY, ''))

    const result = yield race({
        success: take(REPORT_GET_TOKEN_REQUEST_SUCCESS(reportId)),
        failed: take(REPORT_GET_TOKEN_REQUEST_FAILED(reportId)),
    })

    if (result.failed) return null

    const name = _.get(result.success, 'payload.response.body.entity.name', '')

    yield put(setTabText(reportId, MODULE_KEY, name))
    sendMessage('addTabFromNew', { entId: reportId, module: MODULE_KEY, name })

    return null
}

function* onCloseReportById(_action) {
    const reportId = _.get(_action, 'data.id', null)
    if (!reportId) return null

    const isCloseFromTab = _.get(_action, 'data.fromTab', false)

    // if portfolio is closing from tab, don't remove tab
    if (!isCloseFromTab) yield put(removeTab(reportId, MODULE_KEY))

    return null
}

export default function* reportSaga() {
    yield all([
        takeEvery(LOAD_REPORT_MODULE_ACCESS_RIGHTS, loadReportAccessRights),
        takeEvery(isReportAdd, addReport),
        takeEvery(isReportRemove, removeReport),
        takeEvery(REPORT_RIBBON_CHANGE_EVENT, onRibbonButtonsChange),
        takeEvery(BLOCK_DATA_START_LOADING, onBlockDataStartLoading),
        takeEvery(BLOCK_DATA_LOADED, onBlockDataLoaded),
        takeEvery(BLOCK_DATA_LOAD_FAILED, onBlockDataLoadFailed),
        takeEvery(BLOCK_DATA_EMPTY, onBlockDataLoadEmpty),
        takeEvery(BLOCK_DATA_TRANSFORMED, onBlockDataTransformed),
        takeEvery(OPEN_REPORT_BY_ID, onOpenReportById),
        takeEvery(CLOSE_REPORT_BY_ID, onCloseReportById),
        reportRibbonSaga(),
        workspaceSaga(),
    ])
}
